import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageService, ToastMessageOptions } from 'primeng/api';

import CommonUtils from '../../shared/utils/common.utils';

/** Toast helper over PrimeNG MessageService (single app-level p-toast). */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  constructor(private readonly messageService: MessageService) {}

  private held = false;

  /** Freezes the current toast until the page reloads (session expiry), so a
   *  failed request's own error can't replace it. */
  hold(): void {
    this.held = true;
  }

  success(detail: string, summary = 'Success'): void {
    this.show({ severity: 'success', summary, detail });
  }

  info(detail: string, summary = 'Info'): void {
    this.show({ severity: 'info', summary, detail });
  }

  warn(detail: string, summary = 'Warning'): void {
    this.show({ severity: 'warn', summary, detail });
  }

  /** Error toast; a plain `{ message }` wins over the generic HTTP message.
   *  No title by default (matching the old app). */
  error(err: unknown, summary?: string): void {
    const detail =
      err && typeof err === 'object' && 'message' in err &&
      typeof (err as { message?: unknown }).message === 'string' &&
      !(err instanceof HttpErrorResponse)
        ? (err as { message: string }).message
        : CommonUtils.getErrorMessage(err);
    this.show({ severity: 'error', summary, detail });
  }

  /**
   * One toast at a time: the previous is dismissed before the new one shows.
   * Repeated failures (holding down a capped quantity's +) would otherwise
   * stack a column of identical toasts over the page.
   */
  private show(message: ToastMessageOptions): void {
    if (this.held) {
      return;
    }
    this.messageService.clear();
    this.messageService.add(message);
  }
}
